import { useAuthStore } from '../store/authStore';

interface StatCardProps {
  label: string;
  value: string;
  change: string;
  changeType: 'positive' | 'negative' | 'neutral';
}

function StatCard({ label, value, change, changeType }: StatCardProps) {
  const changeColor =
    changeType === 'positive'
      ? 'var(--color-success)'
      : changeType === 'negative'
        ? 'var(--color-danger)'
        : 'var(--color-text-muted)';

  return (
    <div
      style={{
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-lg)',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}
    >
      <span style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>
        {label}
      </span>
      <span style={{ fontSize: '2rem', fontWeight: 700, letterSpacing: '-0.02em' }}>
        {value}
      </span>
      <span style={{ fontSize: '0.8125rem', color: changeColor }}>
        {change}
      </span>
    </div>
  );
}

interface ActivityItem {
  id: string;
  type: 'invoice' | 'client' | 'lead' | 'bid';
  description: string;
  timestamp: string;
}

const MOCK_STATS: StatCardProps[] = [
  { label: 'Total Revenue', value: '$24,580', change: '+12.5% from last month', changeType: 'positive' },
  { label: 'Active Clients', value: '18', change: '+3 new this month', changeType: 'positive' },
  { label: 'Pending Invoices', value: '7', change: '$8,420 outstanding', changeType: 'neutral' },
  { label: 'New Leads', value: '12', change: '+28% from last month', changeType: 'positive' },
];

const MOCK_ACTIVITY: ActivityItem[] = [
  { id: '1', type: 'invoice', description: 'Invoice #1042 paid by Horizon Films', timestamp: '2 hours ago' },
  { id: '2', type: 'client', description: 'New client added: Vertex Productions', timestamp: '5 hours ago' },
  { id: '3', type: 'lead', description: 'Lead "Corporate Documentary" moved to qualified', timestamp: '1 day ago' },
  { id: '4', type: 'bid', description: 'Bid submitted for Wedding Package - Rivera', timestamp: '1 day ago' },
  { id: '5', type: 'invoice', description: 'Invoice #1041 sent to Bloom Media', timestamp: '2 days ago' },
];

function getActivityIcon(type: ActivityItem['type']): string {
  switch (type) {
    case 'invoice': return '\u2B1A';
    case 'client': return '\u2B24';
    case 'lead': return '\u2606';
    case 'bid': return '\u2691';
  }
}

export function DashboardPage() {
  const user = useAuthStore((s) => s.user);

  return (
    <div>
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: '4px' }}>
          Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}
        </h1>
        <p style={{ color: 'var(--color-text-muted)' }}>
          Here is what is happening with your filmmaking business today.
        </p>
      </div>

      {/* Stat cards grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '16px',
          marginBottom: '32px',
        }}
      >
        {MOCK_STATS.map((stat) => (
          <StatCard key={stat.label} {...stat} />
        ))}
      </div>

      {/* Recent activity */}
      <div
        style={{
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)',
          padding: '24px',
        }}
      >
        <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '20px' }}>
          Recent Activity
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {MOCK_ACTIVITY.map((item) => (
            <div
              key={item.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                padding: '12px',
                borderRadius: 'var(--radius-md)',
                background: 'var(--color-bg)',
              }}
            >
              <span
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  background: 'var(--color-surface-hover)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1rem',
                  flexShrink: 0,
                }}
              >
                {getActivityIcon(item.type)}
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.875rem' }}>{item.description}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                  {item.timestamp}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
